import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ROLES = [
  ["client", "Client"],
  ["partner", "Partenaire commerçant"],
  ["courier", "Livreur / Chauffeur"],
  ["artisan", "Artisan"],
  ["vendor", "Vendeur"],
];
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR") : "—");
const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};
const toForm = (u) => ({
  first_name: u.first_name || "",
  last_name: u.last_name || "",
  phone: u.phone || "",
  email: u.email || "",
  roles: u.roles || [],
  active_role: u.active_role || "",
  is_blocked: !!u.is_blocked,
  wallet_balance: u.wallet_balance ?? 0,
  credit_balance: u.credit_balance ?? 0,
  created_at: toLocalInput(u.created_at),
});

const Field = ({ id, label, ...props }) => (
  <div className="space-y-1">
    <Label htmlFor={id} className="text-[10px] font-black uppercase tracking-widest text-slate-400">{label}</Label>
    <Input id={id} data-testid={`profile-${id}-input`} className="bg-slate-900 border-slate-700 text-white" {...props} />
  </div>
);

const History = ({ title, rows, testId, render }) => (
  <section className="space-y-2" data-testid={testId}>
    <h3 className="text-xs font-black uppercase tracking-widest text-amber-400">{title} ({rows.length})</h3>
    {rows.length === 0 ? (
      <p className="text-xs text-slate-500">Aucun élément</p>
    ) : (
      <div className="max-h-56 overflow-y-auto divide-y divide-slate-800 rounded-xl border border-slate-800">
        {rows.map((r) => (
          <div key={r.id} className="px-3 py-2 text-xs text-slate-300 flex justify-between gap-3">{render(r)}</div>
        ))}
      </div>
    )}
  </section>
);

const OrderRow = (o) => (
  <>
    <span className="truncate">#{o.id?.slice(0, 8)} · {o.order_type || "livraison"} · {fmtDate(o.created_at)}</span>
    <span className="shrink-0 font-bold">{o.status} · {Math.round(o.total_amount || 0)} F</span>
  </>
);
const WaveRow = (t) => (
  <>
    <span className="truncate">{t.description || t.type} · {fmtDate(t.created_at)}</span>
    <span className="shrink-0 font-bold">{t.status} · {Math.round(t.amount || 0)} F</span>
  </>
);

export const UserProfileSheet = ({ userId, onClose, onSaved }) => {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!userId) return;
    setData(null);
    axios.get(`${API}/admin/users/${userId}/profile`)
      .then(({ data }) => { setData(data); setForm(toForm(data.user)); })
      .catch((e) => { toast.error(e.response?.data?.detail || "Fiche introuvable"); onClose(); });
  }, [userId, onClose]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));
  const toggleRole = (r) => setForm((f) => ({ ...f, roles: f.roles.includes(r) ? f.roles.filter((x) => x !== r) : [...f.roles, r] }));

  const save = async () => {
    if (!form.roles.length) return toast.error("Au moins un rôle est requis");
    setSaving(true);
    try {
      const initial = toForm(data.user);
      const changed = Object.keys(form).filter((k) => JSON.stringify(form[k]) !== JSON.stringify(initial[k]));
      if (!changed.length) {
        toast.info("Aucune modification");
        return;
      }
      const payload = {};
      changed.forEach((k) => { payload[k] = form[k]; });
      if ("wallet_balance" in payload) payload.wallet_balance = Number(payload.wallet_balance) || 0;
      if ("credit_balance" in payload) payload.credit_balance = Number(payload.credit_balance) || 0;
      if ("created_at" in payload) payload.created_at = payload.created_at ? new Date(payload.created_at).toISOString() : undefined;
      const { data } = await axios.put(`${API}/admin/users/${userId}/profile`, payload);
      setData(data);
      setForm(toForm(data.user));
      toast.success("Fiche mise à jour");
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={!!userId} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto bg-[#0B1220] border-slate-800 text-white" data-testid="user-profile-sheet">
        <SheetHeader>
          <SheetTitle className="text-white">Fiche utilisateur</SheetTitle>
          <SheetDescription className="text-slate-400">Corrigez les informations puis enregistrez.</SheetDescription>
        </SheetHeader>
        {!data || !form ? (
          <div className="flex justify-center py-20"><Loader2 className="animate-spin text-amber-400" /></div>
        ) : (
          <div className="space-y-6 mt-6">
            <div className="grid grid-cols-2 gap-3">
              <Field id="first-name" label="Prénom" value={form.first_name} onChange={set("first_name")} />
              <Field id="last-name" label="Nom" value={form.last_name} onChange={set("last_name")} />
              <Field id="phone" label="Téléphone" value={form.phone} onChange={set("phone")} />
              <Field id="email" label="Email" type="email" value={form.email} onChange={set("email")} />
              <Field id="wallet" label="Solde portefeuille (F)" type="number" min="0" step="1" value={form.wallet_balance} onChange={set("wallet_balance")} />
              <Field id="credits" label="Solde crédits (F)" type="number" min="0" step="1" value={form.credit_balance} onChange={set("credit_balance")} />
              <Field id="created-at" label="Date d'inscription" type="datetime-local" value={form.created_at} onChange={set("created_at")} />
              <div className="space-y-1">
                <Label className="text-[10px] font-black uppercase tracking-widest text-slate-400">Dernière connexion</Label>
                <p className="text-sm py-2" data-testid="profile-last-login">{fmtDate(data.user.last_login_at || data.user.last_seen)}</p>
              </div>
            </div>

            <section className="space-y-2">
              <Label className="text-[10px] font-black uppercase tracking-widest text-slate-400">Rôles (cliquez sur ● pour le profil actif)</Label>
              <div className="grid grid-cols-2 gap-2">
                {ROLES.map(([r, label]) => {
                  const on = form.roles.includes(r);
                  return (
                    <div key={r} className={`flex items-center justify-between rounded-xl border px-3 py-2 ${on ? "border-amber-400/60 bg-amber-400/10" : "border-slate-700"}`}>
                      <button type="button" onClick={() => toggleRole(r)} className="text-xs font-bold text-left" data-testid={`profile-role-${r}-toggle`}>
                        {on ? "✓ " : ""}{label}
                      </button>
                      {on && (
                        <button type="button" onClick={() => set("active_role")(r)} title="Profil actif" data-testid={`profile-active-${r}-btn`}
                          className={`w-3 h-3 rounded-full ${form.active_role === r ? "bg-emerald-400" : "bg-slate-600"}`} />
                      )}
                    </div>
                  );
                })}
              </div>
            </section>

            <div className="flex items-center justify-between rounded-xl border border-slate-700 px-3 py-3">
              <div>
                <p className="text-sm font-bold">Statut du compte</p>
                <p className={`text-xs ${form.is_blocked ? "text-red-400" : "text-emerald-400"}`} data-testid="profile-status-label">{form.is_blocked ? "Suspendu" : "Actif"}</p>
              </div>
              <Switch checked={!form.is_blocked} onCheckedChange={(v) => set("is_blocked")(!v)} data-testid="profile-status-switch" />
            </div>

            <Button onClick={save} disabled={saving} className="w-full bg-amber-400 text-slate-900 hover:bg-amber-300 font-black" data-testid="profile-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}Enregistrer
            </Button>

            <History title="Commandes" rows={data.orders} testId="profile-orders-history" render={OrderRow} />
            <History title="Paiements Wave" rows={data.wave_payments} testId="profile-wave-history" render={WaveRow} />
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
